import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { feStripeFoldToggle } from 'actions/ui.actions';
import Button from 'components/form/Button.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Icon from 'components/generic/Icon.component';
import LINK from 'constants/link.const';
import TRANSFER_POLICY from 'constants/pos/stripeTransferPolicy.const';
import STRIPE_STATUS from 'constants/stripeStatus.const';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import linkTo from 'utils/links/linkTo.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import isNilValue from 'utils/predicates/any/isNilValue.util';

import { getSalonOnlineSettings } from 'settings/online-booking/actions';

import CN from './TipPosAlert.module.scss';

const L = logger('TipPosAlert');

const byIncompleteStylist = (s) =>
  isNilValue(s.stripe_account) &&
  s.stripe_account_status === STRIPE_STATUS.noAccount &&
  s.transfer_policy === TRANSFER_POLICY.noTransfer;

const TipPosAlert = ({ settingsData, isSettingsDataFetched, getSalonOnlineSettings, feStripeFoldToggle }) => {
  const history = useHistory();
  const salonId = useCurrentSalonId();
  const { t } = useTranslation();

  const nav = navFor('TipPosAlert.handleWeekView()', { history });

  const incompleteStylistsCount = Object.values(settingsData.stylist_service_data).filter(byIncompleteStylist).length;
  const totalStylistsCount = Object.keys(settingsData?.stylist_service_data).length;
  const shouldShowAlert = totalStylistsCount > 1 || incompleteStylistsCount !== totalStylistsCount;

  const handleGoToSettingsStripeFold = useCallback(() => {
    nav.push({
      pathname: linkTo({ pattern: LINK.salonOnline, params: { salonId } }),
    });
    feStripeFoldToggle();
  }, [feStripeFoldToggle, nav, salonId]);

  useEffect(() => {
    if (!isSettingsDataFetched) {
      L.unignore(getSalonOnlineSettings(salonId));
    }
  }, [isSettingsDataFetched, getSalonOnlineSettings, salonId]);

  return (
    <div>
      {shouldShowAlert && incompleteStylistsCount > 0 && (
        <div data-bem="TipPosAlert" className={CN.component}>
          <div data-bem="TipPosAlert__title" className={CN.title}>
            {t('slickPay.staffTipping.title')}
          </div>
          <div className={CN.subTitle}>{t('slickPay.staffTipping.description')}</div>
          <AlertPanel tag="TipPosAlert-alert" variant="warningPanel" variantInvert>
            <div className={CN.flex}>
              <div className={CN.alert}>
                <div className={CN.icon}>
                  <Icon variant="error" width="xl" />
                </div>

                <div>
                  {incompleteStylistsCount}/{totalStylistsCount} {t('slickPay.staffTipping.members')}
                  <span className={CN.bold}>{t('slickPay.staffTipping.tipsMessage')}</span>
                </div>
              </div>

              <div className={CN.linkContainer} data-bem="TipPosAlert-link">
                <Button variant="primaryForce" onClick={handleGoToSettingsStripeFold} xSpace>
                  <span className={CN.link}>{t('slickPay.staffTipping.setupButton')}</span>
                </Button>
              </div>
            </div>
          </AlertPanel>
        </div>
      )}
    </div>
  );
};

const TipPosBannerHoc = connect(
  (state) => ({
    settingsData: state.settings.onlineBooking.settingsData,
    isSettingsDataFetched: state.settings.onlineBooking.isSettingsDataFetched,
  }),

  mapActionsToDispatchToProps({
    getSalonOnlineSettings,
    feStripeFoldToggle,
  }),
)(TipPosAlert);

export default TipPosBannerHoc;
